import { z } from 'zod';
import type { LoyaltyPointsRangesToken } from '@sleed/bizcommerce-api';

/**
 * Loyalty points range edit form. Mirrors `LoyaltyPointsRangesToken` and the legacy admin grid
 * (Core/client/admin/src/pages/System/PointRanges/index.js): pnt (points), amn (discount amount)
 * and afrom (active-from) are set on insert only — the legacy grid locks them on edit and only
 * mamn (minimum order amount) stays editable, because the server `SaveLoyaltyPointsRanges` upserts
 * keyed by points ("adding a points value that already exists cancels the previous one"). ato
 * (active-to) is optional. crt (created/updated date) and n_milestone are server-owned (read-only).
 */
// The server's rules (LoyaltyPoints.SaveLoyaltyPointsRanges): whole points above 0, a discount amount (stored negative
// whatever the sign), a minimum of 0 or none, an end after the start.
export const loyaltyRangeSchema = z
	.object({
		id: z.number().optional(),
		pnt: z.number({ message: 'validation.required' }).int('validation.wholeNumber').positive('validation.positiveNumber'),
		amn: z.number({ message: 'validation.required' }).refine(v => v !== 0, 'validation.positiveNumber'),
		mamn: z.number().min(0, 'validation.notNegative').nullable(),
		afrom: z.string().min(1, 'validation.required'),
		ato: z.string().nullable()
	})
	.refine(form => !form.ato || new Date(form.ato) > new Date(form.afrom), { path: ['ato'], message: 'validation.endBeforeStart' });

export type LoyaltyRangeFormValues = z.infer<typeof loyaltyRangeSchema>;

export const EMPTY_LOYALTY_RANGE: LoyaltyRangeFormValues = {
	pnt: 0,
	amn: 0,
	mamn: 0,
	afrom: new Date().toISOString(),
	ato: null
};

export function tokenToForm(token: LoyaltyPointsRangesToken): LoyaltyRangeFormValues {
	return {
		id: token.id,
		pnt: token.pnt ?? 0,
		amn: token.amn ?? 0,
		mamn: token.mamn ?? null,
		afrom: token.afrom ? String(token.afrom) : new Date().toISOString(),
		ato: token.ato ? String(token.ato) : null
	};
}

export function formToToken(form: LoyaltyRangeFormValues): LoyaltyPointsRangesToken {
	return {
		id: form.id,
		pnt: form.pnt,
		amn: form.amn,
		mamn: form.mamn ?? null,
		afrom: form.afrom,
		ato: form.ato || null
	};
}
